'use strict';
import classNames from 'classnames';
import isNumber from 'lodash/isNumber';
import memoize from 'lodash/memoize';
import moment from 'moment';
import 'moment-duration-format';
import * as T from 'prop-types';
import React from 'react';
import {IonSpinner} from '../../../_libs/reactionic/src/components/ionSpinner';
import {Button} from '../../../components/buttons/Button';
import {ClockComponent} from '../../../core-components/clock';
import {GpsStatus} from '../../../core-utilities/enums/GpsStatus';
import {I18nComponent} from '../../../core-utilities/react-i18n/I18nComponent';
import {MINUTE} from '../../utils';


/**
 * @typedef {Object} TaskInfoTimerProps
 * @property {Boolean} [isStopped]
 * @property {Boolean} [isStarted]
 * @property {Number} [startedAt]
 * @property {Number} [totalTime]
 * @property {Boolean} [locCap]
 * @property {Function} onClickStart
 * @property {Function} onClickPause
 * @property {Function} onClickStop
 */

const formatOpts = {trim: false};
const cachedFmtTime = memoize(fmtTime);

export function BaseTaskInfoTimerView(props) {
  if (!props.showTimer) {
    return null;
  }

  const firstButton = props.isStopped
    ? renderStartButton(props, '')
    : renderStopButton(props, 'TaskInfoTimer__button_first');

  let secondButton = null;
  if (!props.isStopped) {
    secondButton = props.isStarted
      ? renderPauseButton(props, 'TaskInfoTimer__button_second')
      : renderResumeButton(props, 'TaskInfoTimer__button_second');
  }

  return (
    <div>
      <div
        className={classNames('TaskInfoTimer', props.className, {
          'TaskInfoTimer_stopped': props.isStopped,
        })}
      >
        {props.displayTime ? (
          <div className={'TaskInfoTimer__time'}>
            <span>
              {cachedFmtTime(getElapsedTime(props))}
            </span>
          </div>
        ) : null}

        {firstButton}
        {secondButton}
      </div>

      {props.displayLoc ? renderLocation(props) : null}
    </div>
  );
}

function renderStartButton(props, className) {
  return (
    <Button
      className={classNames('TaskInfoTimer__button TaskInfoTimer__button_start', className)}
      label={props.startLabel}
      onClick={props.onClickStart}
    />
  );
}

function renderStopButton(props, className) {
  return (
    <Button
      className={classNames('TaskInfoTimer__button TaskInfoTimer__button_stop', className)}
      iconClass="icon ion-stop"
      onClick={props.onClickStop}
    />
  );
}

function renderPauseButton(props, className) {
  return (
    <Button
      className={classNames('TaskInfoTimer__button TaskInfoTimer__button_pause', className)}
      iconClass="icon ion-pause"
      onClick={props.onClickPause}
    />
  );
}

function renderResumeButton(props, className) {
  return (
    <Button
      className={classNames('TaskInfoTimer__button TaskInfoTimer__button_resume', className)}
      iconClass="icon ion-play"
      onClick={props.onClickStart}
    />
  );
}

function renderLocation(props) {
  switch (props.gpsStatus) {
    case GpsStatus.REQUESTING:
      return (
        <div className={classNames('TaskInfoTimer__location', getNavStatusClass(props.gpsStatus))}>
          <i className="icon ion-navigate" />
          <IonSpinner
            className="icon"
            icon="spiral"
          />
        </div>
      );
    case GpsStatus.SUCCESS:
    case GpsStatus.WATCHING:
      return renderSuccessLocation(props);
    case GpsStatus.NOT_SUPPORTED:
    default:
      return props.location ? renderSuccessLocation(props) : null;
  }
}

function renderSuccessLocation(props) {
  return (
    <div
      className={classNames('TaskInfoTimer__location', getNavStatusClass(props), {
        TaskInfoTimer__location_none: !props.locCap,
      })}
    >
      <i className="icon ion-navigate" />
      <span>
        {props.location}
      </span>
    </div>
  );
}

function getNavStatusClass(props) {
  if (props.gpsStatus === GpsStatus.WATCHING || props.gpsStatus === GpsStatus.REQUESTING) {
    return 'TaskInfoTimer__location_watching';
  } else if (props.isStarted) {
    switch (props.gpsStatus) {
      case GpsStatus.NOT_SUPPORTED:
      case GpsStatus.ERROR:
        return 'TaskInfoTimer__location_error';
    }
  }
}

function getElapsedTime(props) {
  const elapsedTime = isNumber(props.totalTime) ? props.totalTime : 0;
  return props.isStarted ? elapsedTime + new Date().getTime() - props.startedAt : elapsedTime;
}

BaseTaskInfoTimerView.propTypes = {
  className: T.string,
  displayTime: T.bool,
  displayLoc: T.bool,
  gpsStatus: T.string,
  totalTime: T.number,
  isStopped: T.bool,
  isStarted: T.bool,
  /** @type {String} Location in human readable form */
  location: T.string,

  onClickStart: T.func,
  onClickPause: T.func,
  onClickStop: T.func,

  startLabel: T.string,
  pauseLabel: T.string,
  resumeLabel: T.string,
  stopLabel: T.string,
};

function fmtTime(date) {
  if (isNumber(date)) {
    return date < 60 * MINUTE
      ? moment.duration(date).format('mm:ss', formatOpts)
      : moment.duration(date).format('D:hh:mm:ss');
  } else {
    return null;
  }
}

export const TaskInfoTimerView = I18nComponent(ClockComponent(BaseTaskInfoTimerView, 1000), [
  {code: 'timers.start.label', prop: 'startLabel'},
  {code: 'timers.pause.label', prop: 'pauseLabel'},
  {code: 'timers.resume.label', prop: 'resumeLabel'},
  {code: 'timers.stop.label', prop: 'stopLabel'},
]);



// WEBPACK FOOTER //
// ./src/js/timers/components/TaskInfoTimer/TaskInfoTimerView.js